import { useAsyncCallback } from '@wix/cli-ui-kit';
import { useState } from 'react';
import { variant } from 'variant';
import type { VariantOf } from 'variant';

export interface TaskStatus {
  status: 'running' | 'done';
  text: string;
}

export interface Task {
  loadingText: string;
  successText: string;
  action: () => Promise<void>;
}

const TaskRunnerStatus = variant({
  Idle: {},
  Running: (runningTasks: Task[], totalTaskCount: number) => {
    const taskStatuses = runningTasks.map((task, index): TaskStatus => {
      const status = index === runningTasks.length - 1 ? 'running' : 'done';
      const text = status === 'done' ? task.successText : task.loadingText;

      return { status, text };
    });

    return {
      tasks: taskStatuses,
      totalTaskCount,
    };
  },
  Done: (tasks: Task[]) => {
    const taskStatuses = tasks.map(
      (task): TaskStatus => ({ status: 'done', text: task.successText })
    );
    return {
      tasks: taskStatuses,
      totalTaskCount: tasks.length,
    };
  },
});

type TaskRunnerStatus = VariantOf<typeof TaskRunnerStatus>;

export function useTaskRunner() {
  const [status, setStatus] = useState<TaskRunnerStatus>(
    TaskRunnerStatus.Idle()
  );

  const { execute } = useAsyncCallback(async (_, tasks: Task[]) => {
    for (const [index, task] of tasks.entries()) {
      setStatus(
        TaskRunnerStatus.Running(tasks.slice(0, index + 1), tasks.length)
      );
      await task.action();
    }
    setStatus(TaskRunnerStatus.Done(tasks));
  });

  return {
    execute,
    status,
  };
}
